import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Trash2, Lock } from "lucide-react";

const EMPTY_FORM = {
  code: "", type: "percent", value: 10,
  billing_interval: "any", assigned_plan: "any",
  valid_from: "", valid_until: "",
  max_uses: "", duration_months: "", active: true, note: "",
};

export default function DiscountCodesPanel() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const isOwner = user?.role === "owner";

  const load = async () => {
    try { setItems((await api.get("/discount-codes")).data); } catch { setItems([]); }
  };
  useEffect(() => { if (isOwner) load(); }, [isOwner]);

  const submit = async () => {
    try {
      const payload = {
        ...form,
        code: form.code.trim().toUpperCase(),
        value: Number(form.value) || 0,
        max_uses: form.max_uses === "" ? null : Number(form.max_uses),
        duration_months: form.duration_months === "" ? null : Number(form.duration_months),
        valid_from: form.valid_from ? new Date(form.valid_from).toISOString() : null,
        valid_until: form.valid_until ? new Date(form.valid_until).toISOString() : null,
        assigned_plan: form.assigned_plan === "any" ? null : form.assigned_plan,
      };
      await api.post("/discount-codes", payload);
      toast.success("OK");
      setOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const del = async (id) => {
    if (!(await confirm())) return;
    await api.delete(`/discount-codes/${id}`);
    load();
  };

  const toggleActive = async (it) => {
    await api.patch(`/discount-codes/${it.id}`, { active: !it.active });
    load();
  };

  if (!isOwner) {
    return (
      <div className="surface rounded-xl p-6 text-sm text-zinc-400 inline-flex items-center gap-2">
        <Lock className="h-4 w-4" /> {t("discount.owner_only")}
      </div>
    );
  }

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
  };

  const typeLabel = (type) => t(`discount.${type}`, { defaultValue: type });

  return (
    <div className="surface rounded-xl p-6" data-testid="discount-codes-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[11px] uppercase tracking-widest text-zinc-500">{t("discount.title")}</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-blue-500 hover:bg-blue-600" data-testid="new-discount-btn">
              <Plus className="h-4 w-4 mr-1" /> {t("discount.new_code")}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#21252E] border-[#2E3241] text-white max-w-lg">
            <DialogHeader><DialogTitle>{t("discount.new_code")}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">{t("discount.code")}</Label>
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder="LAUNCH20"
                  className="bg-[#171B24] border-[#2E3241] uppercase" data-testid="discount-code-input" />
              </div>
              <div>
                <Label className="text-xs">{t("discount.type")}</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="bg-[#171B24] border-[#2E3241]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#21252E] border-[#2E3241] text-white">
                    <SelectItem value="percent">{t("discount.percent")}</SelectItem>
                    <SelectItem value="fixed">{t("discount.fixed")}</SelectItem>
                    <SelectItem value="trial_extension">{t("discount.trial_extension")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{t("discount.value")}</Label>
                <Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })}
                  className="bg-[#171B24] border-[#2E3241]" data-testid="discount-value-input" />
              </div>
              <div>
                <Label className="text-xs">{t("discount.interval")}</Label>
                <Select value={form.billing_interval} onValueChange={(v) => setForm({ ...form, billing_interval: v })}>
                  <SelectTrigger className="bg-[#171B24] border-[#2E3241]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#21252E] border-[#2E3241] text-white">
                    <SelectItem value="any">{t("discount.any")}</SelectItem>
                    <SelectItem value="monthly">{t("billing.monthly")}</SelectItem>
                    <SelectItem value="yearly">{t("billing.yearly")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{t("discount.plan")}</Label>
                <Select value={form.assigned_plan} onValueChange={(v) => setForm({ ...form, assigned_plan: v })}>
                  <SelectTrigger className="bg-[#171B24] border-[#2E3241]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#21252E] border-[#2E3241] text-white">
                    <SelectItem value="any">{t("discount.any")}</SelectItem>
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{t("discount.max_uses")}</Label>
                <Input type="number" value={form.max_uses}
                  onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                  placeholder={t("discount.unlimited")}
                  className="bg-[#171B24] border-[#2E3241]" />
              </div>
              <div>
                <Label className="text-xs">{t("discount.duration_months")}</Label>
                <Input type="number" value={form.duration_months}
                  onChange={(e) => setForm({ ...form, duration_months: e.target.value })}
                  placeholder={t("discount.unlimited")}
                  className="bg-[#171B24] border-[#2E3241]" data-testid="discount-duration-input" />
              </div>
              <div>
                <Label className="text-xs">{t("discount.valid_from")}</Label>
                <Input type="date" value={form.valid_from}
                  onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
                  className="bg-[#171B24] border-[#2E3241]" />
              </div>
              <div>
                <Label className="text-xs">{t("discount.valid_until")}</Label>
                <Input type="date" value={form.valid_until}
                  onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                  className="bg-[#171B24] border-[#2E3241]" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">{t("discount.note")}</Label>
                <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                  className="bg-[#171B24] border-[#2E3241]" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
              <Button onClick={submit} className="bg-blue-500 hover:bg-blue-600" data-testid="create-discount-submit">
                {t("common.create")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-zinc-500">{t("discount.empty")}</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-zinc-400">
            <tr>
              <th className="text-left py-2 text-xs uppercase tracking-widest">{t("discount.code")}</th>
              <th className="text-left py-2 text-xs uppercase tracking-widest">{t("discount.type")}</th>
              <th className="text-left py-2 text-xs uppercase tracking-widest">{t("discount.value")}</th>
              <th className="text-left py-2 text-xs uppercase tracking-widest">{t("discount.current_uses")}</th>
              <th className="text-left py-2 text-xs uppercase tracking-widest">{t("discount.valid_until")}</th>
              <th className="text-left py-2 text-xs uppercase tracking-widest">{t("discount.active")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-t border-[#2E3241]" data-testid={`discount-row-${it.code}`}>
                <td className="py-2 font-mono">{it.code}</td>
                <td className="py-2 text-zinc-400">{typeLabel(it.type)}</td>
                <td className="py-2">
                  {it.type === "percent" ? `${it.value}%` :
                   it.type === "fixed" ? `€${it.value}` :
                   `+${it.value}d`}
                </td>
                <td className="py-2 text-zinc-400">
                  {it.current_uses}{it.max_uses != null ? ` / ${it.max_uses}` : ""}
                </td>
                <td className="py-2 text-zinc-400">{fmtDate(it.valid_until)}</td>
                <td className="py-2">
                  <button
                    type="button"
                    onClick={() => toggleActive(it)}
                    data-testid={`discount-toggle-${it.code}`}
                  >
                    <Badge className={it.active
                      ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                      : "bg-zinc-500/15 text-zinc-400 border-zinc-500/30"}>
                      {it.active ? t("discount.active") : t("discount.inactive")}
                    </Badge>
                  </button>
                </td>
                <td className="py-2 text-right">
                  <Button size="icon" variant="ghost" onClick={() => del(it.id)}
                    className="text-zinc-400 hover:text-red-400"
                    data-testid={`discount-delete-${it.code}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
